import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import { useAppStore } from '~/stores/app'
import { DialogId } from '~/types/common'
import type { Skill } from '~/types/skill'

type SkillDetailDialogProps = {
  item?: Skill | null
  onEdit: (item: Skill) => void
  onDelete: (item: Skill) => void
}

export function SkillDetailDialog({ item, onEdit, onDelete }: SkillDetailDialogProps) {
  const { t } = useTranslation()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const openDialog = useAppStore((state) => state.openDialog)
  const closeDialog = useAppStore((state) => state.closeDialog)

  const isOpen = openDialogIds.includes(DialogId.DETAIL_SKILL)

  if (!item) return null

  const handleEdit = () => {
    closeDialog(DialogId.DETAIL_SKILL)
    onEdit(item)
    openDialog(DialogId.UPDATE_SKILL)
  }

  const handleDelete = () => {
    closeDialog(DialogId.DETAIL_SKILL)
    onDelete(item)
    openDialog(DialogId.DELETE_SKILL)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(DialogId.DETAIL_SKILL)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{item.nameEn}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('skills.icon')}</Label>
            <div>
              {item.iconUrl ? (
                <img src={item.iconUrl} alt="" className="h-16 w-16 rounded-md border object-cover" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-md border bg-muted text-xs text-muted-foreground">
                  {t('skills.noIcon')}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('skills.nameEn')}</Label>
            <p className="font-medium">{item.nameEn}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('skills.nameJa')}</Label>
            <p className="font-medium">{item.nameJa}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('skills.category')}</Label>
            <p className="font-medium">{item.category ? t(`skillCategory.${item.category.slug}`) : '-'}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('common.status')}</Label>
            <div>
              <Badge variant={item.isActive ? 'success' : 'secondary'}>
                {item.isActive ? t('common.active') : t('common.inactive')}
              </Badge>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground">{t('common.createdAt')}</Label>
            <p>{new Date(item.createdAt).toLocaleString()}</p>
          </div>
        </div>
        <DialogFooter>
          {item.schoolId ? (
            <p className="self-center text-sm text-muted-foreground">{t('skills.managedOnSchoolsPage')}</p>
          ) : (
            <>
              <Button variant="outline" onClick={handleEdit}>
                {t('common.edit')}
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                {t('common.delete')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
